import { Grid, MenuItem, Select, SelectChangeEvent, Tab, Table, TableBody } from "@mui/material";
import * as api from "api/Core";
import { ChartJsHeight, ChartJsOptions, DefaultListParameters } from "assets/appConfig";
import { DashboardPatterns } from "assets/jss/common-styles";
import { AppMessageBox } from "components/AppMessageBox";
import { AppTabs } from "components/AppTabs";
import IconButtonCancel from "components/Buttons/IconButtonCancel";
import IconButtonDownload from "components/Buttons/IconButtonDownload";
import { SpinnerIndicator } from "components/CallServer";
import DashDoughnutBox from "components/Dashboard/DashDoughnutBox";
import { exportToXLSX } from "components/ExportToExcel";
import { JoinComponents } from "components/JoinComponents";
import MuiMessageBox from "components/MuiExtras/MessageBox/MuiMessageBox";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiPagination from "components/MuiExtras/MuiPagination";
import MuiSelectFilter from "components/MuiExtras/MuiSelectFilter";
import MuiMuted from "components/MuiExtras/Typography/MuiMuted";
import MuiReverse from "components/MuiExtras/Typography/MuiReverse";
import { Formaters } from "lib/Formarters";
import HtmlUtils from "lib/HtmlUtils";
import LibUtils from "lib/LibUtils";
import { DashboardCourseAnalysisDetailColumn, DashboardCourseAnalysisFilter, DashboardCourseAnalysisSummaryColumn, DashboardGraphByGroupModel, DashboardGraphModel, DashboardPublicationModel, DashboardTopicModel, DashboardUserModel, DashboardUserProgressModel, PagedResult } from "models/ApiModels";
import moment from "moment";
import * as React from "react";
import { Bar } from "react-chartjs-2";
import { WithTranslation } from "react-i18next";
import PublicationListDrawer from "views/Dashboards/components/PublicationListDrawer";
import { RenderBarGraph } from "views/Dashboards/components/RenderBarGraph";
import { RenderLineGraph } from "views/Dashboards/components/RenderLineGraph";
import { RenderPublicationModelList, RenderPublicationModelListEvents } from "views/Dashboards/components/RenderPublicationModelList";
import { RenderTopicModelList, RenderTopicModelListEvents } from "views/Dashboards/components/RenderTopicModelList";
import { RenderUserModelList, RenderUserModelListEvents } from "views/Dashboards/components/RenderUserModelList";
import { RenderUserProgressModelList } from "views/Dashboards/components/RenderUserProgressModelList";
import UserListDrawer from "views/Dashboards/components/UserListDrawer";
import { DashboardCourseAnalysisGraphPeriod, DashboardCourseAnalysisListPeriod, IDashboardCourseAnalysisListProps } from "views/Dashboards/CourseAnalysisDashboard/Common";
import { ParticipationListPublicationPeriod } from "views/Dashboards/UserAndGroupsDashboard/Common";

/////////////////////////////////////////////////////////////////////////////////////

type GraphOption = DashboardCourseAnalysisSummaryColumn.Progress | DashboardCourseAnalysisSummaryColumn.Completed | DashboardCourseAnalysisSummaryColumn.Deadline;

interface DashboardCourseAnalysisProgressState {
    data: DashboardGraphByGroupModel | DashboardGraphModel[];
    option: GraphOption;
    primaryOption: DashboardCourseAnalysisDetailColumn;
    secondOption: number;
    selectedDay: Date;
    tabOption: DashboardCourseAnalysisGraphPeriod;
    loading: boolean;
}

export interface DashboardCourseAnalysisProgressProps extends RenderPublicationModelListEvents, RenderTopicModelListEvents, RenderUserModelListEvents {
    tile: DashboardCourseAnalysisSummaryColumn;
    filter: DashboardCourseAnalysisFilter;
}


type Props = DashboardCourseAnalysisProgressProps & WithTranslation;

export class DashboardCourseAnalysisProgressGraph extends React.PureComponent<Props, DashboardCourseAnalysisProgressState>{

    constructor(props: Props) {
        super(props);

        this.state = {
            data: null,
            option: DashboardCourseAnalysisSummaryColumn.Progress,
            loading: true,
            primaryOption: DashboardCourseAnalysisDetailColumn.CoursesMostCompleted,
            secondOption: null,
            selectedDay: null,
            tabOption: DashboardCourseAnalysisGraphPeriod.Last30
        };
    }

    //
    private res(r: string) {
        return this.props.t("Views.Dashboards.CourseAnalysis.ProgressGraph." + r);
    }

    ///
    private getDateFilter() {

        if (this.state.option != DashboardCourseAnalysisSummaryColumn.Completed)
            return null;

        var de = moment().utc().endOf("day");
        var ds = moment().utc().startOf("day");
        ds = this.state.tabOption == DashboardCourseAnalysisGraphPeriod.Last30
            ? ds.add(-29, "days")
            : ds.add(-12, "months");

        return { startDate: ds.toDate(), endDate: de.toDate() };
    }

    ///
    private loadData() {
        const { option, tabOption } = this.state;

        api.getDashboardCourseAnalysis({

            columnToSummary: option,

            detailColumn: option !== DashboardCourseAnalysisSummaryColumn.Completed
                ? DashboardCourseAnalysisDetailColumn.GraphByGroup
                : tabOption == DashboardCourseAnalysisGraphPeriod.Last30
                    ? DashboardCourseAnalysisDetailColumn.GraphByDay
                    : DashboardCourseAnalysisDetailColumn.GraphByMonth,

            ...this.props.filter,
            ...this.getDateFilter(),
            range: null,
        })
            .then((result) => {
                if (option == DashboardCourseAnalysisSummaryColumn.Completed) {
                    (result as DashboardGraphModel[]).forEach((v) => {
                        v.day = v.day.convertLocalToUtcDate();
                    });
                }
                this.setState({ data: result, loading: false });
            })
            .catch((error) => {
                this.setState({ data: null, loading: false });
            });
    }

    ///
    private handleOption = (e: SelectChangeEvent<GraphOption>) => {
        this.setState({ option: e.target.value as GraphOption, loading: true }, () => this.loadData());
    };

    ///
    private renderProgressGraph() {

        const data = this.state.data as DashboardGraphByGroupModel;

        var ds: number[] = [];
        var labels: string[] = [];
        var backgroundColors: string[] = [];

        var bc = DashboardPatterns.knowlegdeBars;

        ds = [data.f0, data.f1, data.f2, data.f3, data.f4, data.f5];

        backgroundColors = [bc.f0.backgroundColor, bc.f1.backgroundColor, bc.f2.backgroundColor, bc.f3.backgroundColor, bc.f4.backgroundColor, bc.f5.backgroundColor];
        labels = [this.props.t("Views.Dashboards.CourseAnalysis.ProgressGraph.NotAccessLabel"), "0-24%", "25-49%", "50-74%", "75-99%", "100%"];

        const total = ds.length == 0 ? 0 : ds.reduce((p, c) => p + c);
        const d = ds.map((v, index) => { return { label: labels[index], backgroundColor: backgroundColors[index], data: [total == 0 ? 0 : v / total * 100.0] }; });


        let stepWidth = 1;
        let maxValue = Math.max(...ds);
        if (maxValue > 0) {
            if (maxValue % 10 !== 0) maxValue += 10 - maxValue % 10;
            stepWidth = Math.ceil(maxValue / 10);
        }
        else maxValue = null;

        return (
            <MuiContainer>

                <div id="graphCanvas" style={{ position: "relative", height: 150, width: "100%" }}>
                    <Bar
                        data={
                            {
                                labels: [this.props.t("Views.Dashboards.Topic.KnowledgeDatabase.DistributionChartLabel")],
                                datasets: d
                            }
                        }
                        options={
                            {
                                ...ChartJsOptions,
                                indexAxis: "y",
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: {
                                    legend: {
                                        display: true,
                                        position: "bottom"
                                    },
                                    tooltip: {
                                        callbacks: {
                                            label: function (item: any) {
                                                return `${item.chart.data.datasets[item.datasetIndex].label}: ${parseFloat(item.formattedValue).toFixed(1)}% (${ds[item.datasetIndex]})`;
                                            },
                                        }
                                    }
                                },
                                scales: {
                                    x: {
                                        display: true,
                                        stacked: true,
                                        title: {
                                            display: false,
                                        },
                                        min: 0,
                                        max: 100,
                                        grid: {
                                            display: false
                                        },
                                        ticks: {
                                            display: false
                                        }
                                    },
                                    y: {
                                        display: false,
                                        stacked: true,
                                        title: {
                                            display: false,
                                        }
                                    }
                                }
                            }
                        }

                        getElementsAtEvent={
                            (e) => {
                                if (e.length > 0) {
                                    this.setState({ primaryOption: DashboardCourseAnalysisDetailColumn.EnrollProgress, secondOption: (e[0] as any).index });
                                }
                            }
                        }
                    />
                </div>
            </MuiContainer>
        );
    }

    ///
    private renderProgressGauges() {
        const { loading } = this.state;
        const data = this.state.data as DashboardGraphByGroupModel;
        const total = data.f0 + data.f1 + data.f2 + data.f3 + data.f4 + data.f5;

        return (
            <MuiContainer>
                <Grid container justifyContent="space-between" alignItems="center" >
                    <Grid item>
                        {
                            loading
                                ? <SpinnerIndicator />
                                : <DashDoughnutBox
                                    key="1"
                                    active={false}
                                    title={<span>{this.res("NotAccessLabel")}<br /><br /></span>}
                                    titlePosition="top"
                                    value={100.0 * data.f0 / total}
                                    color="muted"
                                    onClick={() => this.setState({ primaryOption: DashboardCourseAnalysisDetailColumn.UsersNotAccess, secondOption: null })}
                                />
                        }
                    </Grid>

                    <Grid item>
                        {
                            loading
                                ? <SpinnerIndicator />
                                : <DashDoughnutBox
                                    key="2"
                                    active={false}
                                    title={<span><JoinComponents components={this.res("NoProgressAbove30Label").split('\n')} separator={<br />} /> </span>}
                                    titlePosition="top"
                                    value={100.0 * data.f6 / total}
                                    color="error"
                                    onClick={() => this.setState({ primaryOption: DashboardCourseAnalysisDetailColumn.UsersMostAbandon, secondOption: null })}
                                />
                        }
                    </Grid>

                    <Grid item>
                        {
                            loading
                                ? <SpinnerIndicator />
                                : <DashDoughnutBox
                                    key="3"
                                    active={false}
                                    title={<span>{this.res("CompletedLabel")}<br /><br /></span>}
                                    titlePosition="top"
                                    value={100.0 * data.f5 / total}
                                    color="success"
                                    onClick={() => this.setState({ primaryOption: DashboardCourseAnalysisDetailColumn.UsersMostCompleted, secondOption: null })}
                                />
                        }
                    </Grid>
                </Grid>
            </MuiContainer>
        );
    }

    ///
    private renderDeadlineGraph() {
        const data = this.state.data as DashboardGraphByGroupModel;

        var ds: number[] = [];
        var labels: string[] = [];
        var backgroundColors: string[] = [];

        var bc = DashboardPatterns.knowlegdeBars;

        ds = [data.f1, data.f2, data.f3, data.f4];

        backgroundColors = [bc.f1.backgroundColor, bc.f2.backgroundColor, bc.f3.backgroundColor, bc.f4.backgroundColor];
        labels = [
            `1-7 ${this.props.t("Glossary.Days")}`,
            `8-15 ${this.props.t("Glossary.Days")}`,
            `16-30 ${this.props.t("Glossary.Days")}`,
            `${this.props.t("Common.Literals.MoreThan")} 30 ${this.props.t("Glossary.Days")}`
        ];

        const total = ds.length == 0 ? 0 : ds.reduce((p, c) => p + c);
        const d = ds.map((v, index) => { return { label: labels[index], backgroundColor: backgroundColors[index], data: [total == 0 ? 0 : v / total * 100.0] }; });

        let stepWidth = 1;
        let maxValue = Math.max(...ds);
        if (maxValue > 0) {
            if (maxValue % 10 !== 0) maxValue += 10 - maxValue % 10;
            stepWidth = Math.ceil(maxValue / 10);
        }
        else maxValue = null;

        return (
            <MuiContainer>
                <div style={{ position: "relative", height: 150, width: "100%" }}>
                    <Bar
                        data={
                            {
                                labels: [this.props.t("Views.Dashboards.Topic.KnowledgeDatabase.DistributionChartLabel")],
                                datasets: d
                            }
                        }
                        options={
                            {
                                ...ChartJsOptions,
                                indexAxis: "y",
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: {
                                    legend: {
                                        display: true,
                                        position: "bottom"
                                    },
                                    tooltip: {
                                        callbacks: {
                                            label: function (item: any) {
                                                return `${item.chart.data.datasets[item.datasetIndex].label}: ${parseFloat(item.formattedValue).toFixed(1)}% (${ds[item.datasetIndex]})`;
                                            },
                                        }
                                    }
                                },
                                scales: {
                                    x: {
                                        display: true,
                                        stacked: true,
                                        title: {
                                            display: false,
                                        },
                                        min: 0,
                                        max: 100,
                                        grid: {
                                            display: false
                                        },
                                        ticks: {
                                            display: false
                                        }
                                    },
                                    y: {
                                        display: false,
                                        stacked: true,
                                        title: {
                                            display: false,
                                        }
                                    }
                                }
                            }
                        }

                        getElementsAtEvent={
                            (e) => {
                                if (e.length > 0) {
                                    this.setState({ primaryOption: DashboardCourseAnalysisDetailColumn.EnrollCompleted, secondOption: (e[0] as any).index });
                                }
                            }
                        }
                    />
                </div>
            </MuiContainer>
        );
    }

    ///
    private renderCompletedGraph() {
        const data = this.state.data as DashboardGraphModel[];
        return this.state.tabOption == DashboardCourseAnalysisGraphPeriod.Last30
            ? <RenderLineGraph color="blue" data={data} onSelectDay={(dt) => this.setState({ selectedDay: dt })} />
            : <RenderBarGraph color="blue" data={data} lastItemFade={0.6} onSelectDay={(dt) => this.setState({ selectedDay: dt })} />;
    }

    ///
    private checkForUpdateState(prevProps: Props) {
        if (!LibUtils.isEqual(this.props.filter, prevProps.filter)) {
            this.setState({ loading: true }, () => this.loadData());
        }
    }

    ///
    public componentDidMount() {
        this.loadData();
    }

    ///
    public componentDidUpdate(prevProps: Props) {
        this.checkForUpdateState(prevProps);
    }

    ///
    public render() {
        const { loading, data, option } = this.state;

        if (!loading && data == null)
            return <AppMessageBox resource="LoadGraphDataFailed" />;

        let subtitle = "";

        switch (option) {
            case DashboardCourseAnalysisSummaryColumn.Completed:
                subtitle = this.res("CompletedSubtitle");
                break;
            case DashboardCourseAnalysisSummaryColumn.Deadline:
                subtitle = this.res("DeadlineSubtitle");
                break;
            default:
                subtitle = this.res("DefaultSubtitle");
                break;
        }

        return (
            <>
                <MuiContainer margin="dense" >
                    <Grid container justifyContent="space-between">
                        <Grid item>
                            <MuiContainer margin="dense" >
                                <Select value={option} onChange={this.handleOption} style={{ fontSize: "1.5em" }}>
                                    <MenuItem value={DashboardCourseAnalysisSummaryColumn.Progress}>{this.res("ProgressOption").toLocaleUpperCase()}</MenuItem>
                                    <MenuItem value={DashboardCourseAnalysisSummaryColumn.Completed}>{this.res("CompletedOption").toLocaleUpperCase()}</MenuItem>
                                    <MenuItem value={DashboardCourseAnalysisSummaryColumn.Deadline}>{this.res("DeadlineOption").toLocaleUpperCase()}</MenuItem>
                                </Select>
                                <div>
                                    <MuiMuted>{subtitle}</MuiMuted>
                                </div>
                            </MuiContainer>
                        </Grid>

                        {
                            option == DashboardCourseAnalysisSummaryColumn.Completed &&
                            <Grid item>
                                <AppTabs
                                    value={this.state.tabOption}
                                    onChange={
                                        (e, newValue: DashboardCourseAnalysisGraphPeriod) => {
                                            this.setState({ tabOption: newValue, selectedDay: null, loading: true }, () => this.loadData());
                                        }
                                    }
                                    indicatorColor="primary"
                                    textColor="primary"
                                >
                                    <Tab label={this.props.t<string>("Views.Dashboards.Common.Days30")} value={DashboardCourseAnalysisGraphPeriod.Last30} />
                                    <Tab label={this.props.t<string>("Views.Dashboards.Common.Months12")} value={DashboardCourseAnalysisGraphPeriod.Last12} />
                                </AppTabs>
                            </Grid>
                        }
                    </Grid>
                </MuiContainer>

                {
                    loading
                        ? <SpinnerIndicator />
                        : option == DashboardCourseAnalysisSummaryColumn.Progress
                            ? (
                                <>
                                    {this.renderProgressGraph()}
                                    {this.renderProgressGauges()}
                                </>
                            )
                            : option == DashboardCourseAnalysisSummaryColumn.Deadline
                                ? this.renderDeadlineGraph()
                                : this.renderCompletedGraph()
                }

                <DashboardCourseAnalysisProgressList
                    {...this.props}
                    tile={this.state.option}
                    primaryOption={this.state.primaryOption}
                    secondOption={this.state.secondOption}
                    selectedDay={this.state.selectedDay}
                    graphPeriod={this.state.tabOption}
                    onCancelDay={() => this.setState({ selectedDay: null })}
                />

            </>
        );
    }
}

/////////////////////////////////////////////////////////////////////////////////////////////

interface TIListState {
    dialog: any;
    data: PagedResult<any>;
    offset: number;
    loading: boolean;
    primaryListOption: DashboardCourseAnalysisDetailColumn;
    secondaryListOption: number;
    tabOption: DashboardCourseAnalysisListPeriod;
}

interface IListProps extends IDashboardCourseAnalysisListProps {
    primaryOption?: DashboardCourseAnalysisDetailColumn;
    secondOption: number;
}

type ListProps = IListProps & WithTranslation;

export class DashboardCourseAnalysisProgressList extends React.PureComponent<ListProps, TIListState> {

    constructor(props: ListProps) {
        super(props);

        this.state = {
            dialog: null,
            data: null,
            offset: null,
            loading: true,
            tabOption: DashboardCourseAnalysisListPeriod.Last30,
            ...this.computeListOptions()
        };
    }

    //
    private res(r: string) {
        return this.props.t("Views.Dashboards.CourseAnalysis.ProgressList." + r);
    }

    ///
    private getDateFilter() {

        if (this.props.tile != DashboardCourseAnalysisSummaryColumn.Completed)
            return null;

        if (this.props.selectedDay) {
            if (this.props.graphPeriod == DashboardCourseAnalysisGraphPeriod.Last30)
                return {
                    startDate: this.props.selectedDay.convertUtcToLocalDate(),
                    endDate: this.props.selectedDay.putEndOfDay().convertUtcToLocalDate()
                };

            if (this.props.graphPeriod == DashboardCourseAnalysisGraphPeriod.Last12) {
                var lastDay = moment(this.props.selectedDay);
                lastDay = lastDay.endOf('month');

                return {
                    startDate: this.props.selectedDay.convertUtcToLocalDate(),
                    endDate: lastDay.toDate().convertUtcToLocalDate()
                };
            }
        }

        if (this.state.tabOption == DashboardCourseAnalysisListPeriod.Last30) {
            const lastDay = moment().utc().endOf("day");
            const firstDay = moment().utc().startOf("day");
            firstDay.add(-29, "days");

            return {
                startDate: firstDay.toDate(),
                endDate: lastDay.toDate()
            };
        }

        return {
            startDate: null,
            endDate: null
        };
    }

    ///
    private loadData(offset: number, scrollToStartOfList: boolean = false) {

        this.setState({ loading: true });

        api.getDashboardCourseAnalysis(
            {
                columnToSummary: DashboardCourseAnalysisSummaryColumn.CommonProgress,
                detailColumn: this.state.primaryListOption,
                selectedValue: this.state.secondaryListOption,
                range: { offset, take: DefaultListParameters.viewRecordsByPage },
                ...this.getDateFilter(),
                ...this.props.filter
            })
            .then((result) => {
                this.setState({ data: result, loading: false, offset: offset }, () => {
                    if (scrollToStartOfList) HtmlUtils.scrollToElement("divList");
                });

            })
            .catch((error) => {
                this.setState({ data: null, loading: false, offset: 0 });
            });
    }

    ///
    private handleExport = () => {

        api.getDashboardCourseAnalysis(
            {
                columnToSummary: DashboardCourseAnalysisSummaryColumn.CommonProgress,
                detailColumn: this.state.primaryListOption,
                selectedValue: this.state.secondaryListOption,
                range: { offset: 0, take: 32767 },
                ...this.getDateFilter(),
                ...this.props.filter
            })
            .then((result) => {
                let csv: any;
                const fileName = 'CourseAnalysisProgress-' + moment().toISOString();

                switch (this.state.primaryListOption) {

                    case DashboardCourseAnalysisDetailColumn.Topics:
                        csv = (result.items as DashboardTopicModel[]).map((v) => { return { name: v.name, total: v.total }; });
                        break;

                    case DashboardCourseAnalysisDetailColumn.UsersMostCompleted:
                    case DashboardCourseAnalysisDetailColumn.UsersMostAbandon:
                    case DashboardCourseAnalysisDetailColumn.UsersNotAccess:
                        csv = (result.items as DashboardUserModel[]).map((v) => { return { name: v.name, total: v.total }; });
                        break;

                    case DashboardCourseAnalysisDetailColumn.UsersProgressCourse:
                        csv = (result.items as DashboardUserProgressModel[]).map((v) => { return { name: v.name, progress: v.startedOn ? `${v.progress}%` : this.props.t("Views.Dashboards.CourseAnalysis.ProgressList.NotStarted")  }; });
                        break;

                    case DashboardCourseAnalysisDetailColumn.CoursesMostAbandon:
                    case DashboardCourseAnalysisDetailColumn.CoursesMostCompleted:
                        csv = (result.items as DashboardPublicationModel[]).map((v) => { return { title: v.title, creationDate: v.creationDate, publishDate: v.publishDate, creatorName: v.creatorName, firstTopicName: v.firstTopicName, total: v.total }; });
                        break;

                    case DashboardCourseAnalysisDetailColumn.EnrollProgress:
                    case DashboardCourseAnalysisDetailColumn.EnrollCompleted:
                        csv = this.props.filter.courseId == null
                            ? (result.items as DashboardPublicationModel[]).map((v) => { return { title: v.title, creationDate: v.creationDate, publishDate: v.publishDate, creatorName: v.creatorName, firstTopicName: v.firstTopicName, total: v.total }; })
                            : (result.items as DashboardUserModel[]).map((v) => { return { name: v.name, total: v.total }; });
                        break;
                }

                if (csv.length > 0) exportToXLSX(csv, fileName);

            });
    };

    ///
    private handleListOption = (e: SelectChangeEvent<DashboardCourseAnalysisDetailColumn>) => {
        this.setState({
            primaryListOption: e.target.value as DashboardCourseAnalysisDetailColumn,
            secondaryListOption: e.target.value == DashboardCourseAnalysisDetailColumn.EnrollCompleted || e.target.value == DashboardCourseAnalysisDetailColumn.EnrollProgress ? 0 : null
        }, () => this.loadData(0));
    };

    ///
    private handleSecondaryListOption = (e: SelectChangeEvent<number>) => {
        this.setState({ secondaryListOption: e.target.value as number }, () => this.loadData(0));
    };

    ///
    private getPrimaryListOptions() {

        const { filter } = this.props;

        var result: Array<{ key: DashboardCourseAnalysisDetailColumn, text: string; }> = [];

        if (this.props.tile == DashboardCourseAnalysisSummaryColumn.Completed) {
            if (filter.courseId == null) {
                result.push({ key: DashboardCourseAnalysisDetailColumn.CoursesMostCompleted, text: this.res("CoursesMostCompletedOption") });
                result.push({ key: DashboardCourseAnalysisDetailColumn.UsersMostCompleted, text: this.res("UsersMostCompletedOption") });
            }
            else {
                result.push({ key: DashboardCourseAnalysisDetailColumn.UsersMostCompleted, text: this.res("UsersMostCompleted2Option") });
            }
        }
        else if (this.props.tile == DashboardCourseAnalysisSummaryColumn.Deadline) {
            if (filter.courseId == null) {
                result.push({ key: DashboardCourseAnalysisDetailColumn.CoursesMostAbandon, text: this.res("CoursesMostAbandonOption") });
                result.push({ key: DashboardCourseAnalysisDetailColumn.UsersMostAbandon, text: this.res("UsersMostAbandonOption") });
            }
            else {
                result.push({ key: DashboardCourseAnalysisDetailColumn.UsersMostAbandon, text: this.res("UsersMostAbandon2Option") });
            }
            result.push({ key: DashboardCourseAnalysisDetailColumn.EnrollCompleted, text: this.res("EnrollCompletedOption") });
        }
        else {
            if (filter.courseId == null) {
                result.push({ key: DashboardCourseAnalysisDetailColumn.CoursesMostCompleted, text: this.res("CoursesMostCompletedOption") });
                result.push({ key: DashboardCourseAnalysisDetailColumn.CoursesMostAbandon, text: this.res("CoursesMostAbandonOption") });
                result.push({ key: DashboardCourseAnalysisDetailColumn.UsersMostCompleted, text: this.res("UsersMostCompletedOption") });
                result.push({ key: DashboardCourseAnalysisDetailColumn.UsersMostAbandon, text: this.res("UsersMostAbandonOption") });
                result.push({ key: DashboardCourseAnalysisDetailColumn.UsersNotAccess, text: this.res("UsersNotAccessOption") });
            }
            else {
                result.push({ key: DashboardCourseAnalysisDetailColumn.UsersProgressCourse, text: this.res("UsersProgressCourseOption") });
                result.push({ key: DashboardCourseAnalysisDetailColumn.UsersMostCompleted, text: this.res("UsersMostCompleted2Option") });
                result.push({ key: DashboardCourseAnalysisDetailColumn.UsersMostAbandon, text: this.res("UsersMostAbandon2Option") });
                result.push({ key: DashboardCourseAnalysisDetailColumn.UsersNotAccess, text: this.res("UsersNotAccess2Option") });
            }

            if (filter.topicId == null && filter.courseId == null) {
                result.push({ key: DashboardCourseAnalysisDetailColumn.Topics, text: this.res("TopicsOption") });
            }

            result.push({ key: DashboardCourseAnalysisDetailColumn.EnrollProgress, text: this.res("EnrollProgressOption") });

            result.push({ key: DashboardCourseAnalysisDetailColumn.EnrollCompleted, text: this.res("EnrollCompletedOption") });

        }
        return result;
    }

    ///
    private getSecondaryListOptions() {
        if (this.state.primaryListOption == DashboardCourseAnalysisDetailColumn.EnrollProgress)
            return [
                { key: 0, text: this.props.t("Views.Dashboards.CourseAnalysis.ProgressGraph.NotAccessLabel") },
                { key: 1, text: "0-24%" },
                { key: 2, text: "25-49%" },
                { key: 3, text: "50-74%" },
                { key: 4, text: "75-99%" },
                { key: 5, text: "100%" }
            ];

        if (this.state.primaryListOption == DashboardCourseAnalysisDetailColumn.EnrollCompleted) {
            const days = this.props.t("Glossary.Days");
            return [
                { key: 0, text: `0-7 ${days}` },
                { key: 1, text: `8-15 ${days}` },
                { key: 2, text: `16-30 ${days}` },
                { key: 3, text: `> 30 ${days}` },
            ];
        }

        return null;
    }

    ///
    private computeListOptions() {
        const listOptions = this.getPrimaryListOptions();

        let primaryOption = listOptions.find(p => p.key == this.props.primaryOption);
        if (primaryOption == null) primaryOption = listOptions[0];

        const secondOption = primaryOption.key == DashboardCourseAnalysisDetailColumn.EnrollCompleted || primaryOption.key == DashboardCourseAnalysisDetailColumn.EnrollProgress
            ? this.props.secondOption
            : null;

        return { primaryListOption: primaryOption.key, secondaryListOption: secondOption };
    }

    ///
    private publicationDrillDown(id: string, name: string, detailColumn: DashboardCourseAnalysisDetailColumn) {
        this.setState({
            dialog: (
                <PublicationListDrawer
                    title="Cursos"
                    subtitle={name}
                    showCreationDate={false}
                    detailCountText={{ singular: "inscrição", plural: "inscrições" }}
                    onGetData={
                        (searchtext, offset) =>
                            api.getDashboardCourseAnalysis({
                                columnToSummary: DashboardCourseAnalysisSummaryColumn.CommonProgress,
                                detailColumn: detailColumn,
                                ...this.props.filter,
                                selectedValue: this.state.secondaryListOption,
                                drillDownFilter: {
                                    id: id,
                                    title: searchtext,
                                },
                                range: { offset: offset, take: 20 },
                            })
                    }
                    onClose={
                        () => this.setState({ dialog: null })
                    }
                />
            )
        });
    }

    ///
    private userDrillDown(id: string, name: string, detailColumn: DashboardCourseAnalysisDetailColumn) {
        this.setState({
            dialog: (
                <UserListDrawer
                    title={this.res("UserListDrawerTitle")}
                    subtitle={name}
                    onGetData={
                        (searchtext, offset) =>
                            api.getDashboardCourseAnalysis({
                                columnToSummary: DashboardCourseAnalysisSummaryColumn.CommonProgress,
                                detailColumn: detailColumn,
                                ...this.props.filter,
                                selectedValue: this.state.secondaryListOption,
                                drillDownFilter: {
                                    id: id,
                                    title: searchtext,
                                },
                                range: { offset: offset, take: 20 },
                            })
                    }
                    onClose={
                        () => this.setState({ dialog: null })
                    }
                />
            )
        });
    }

    ///
    private renderHeaderTab() {
        const sDay = this.props.selectedDay && this.props.graphPeriod == DashboardCourseAnalysisGraphPeriod.Last30
            ? Formaters.capitalize(this.props.t("Glossary.Day")) + " " + moment(this.props.selectedDay).format("DD/MMM")
            : this.props.selectedDay && this.props.graphPeriod == DashboardCourseAnalysisGraphPeriod.Last12
                ? moment(this.props.selectedDay).format("MMM/YYYY")
                : null;

        return this.props.selectedDay
            ? (
                <MuiReverse color="warning">
                    <b>{sDay}</b>
                    <IconButtonCancel
                        size="small"
                        onClick={() => this.props.onCancelDay()}
                    />
                </MuiReverse>
            )
            : (
                <Grid item>
                    <AppTabs
                        value={this.state.tabOption}
                        onChange={
                            (e, newValue: DashboardCourseAnalysisListPeriod) => {

                                this.setState({ tabOption: newValue }, () => {
                                    this.loadData(0, true);
                                });

                            }
                        }
                        indicatorColor="primary"
                        textColor="primary"
                    >
                        <Tab label={this.props.t<string>("Views.Dashboards.Common.LastMonth")} value={ParticipationListPublicationPeriod.Last30} />
                        <Tab label={this.props.t<string>("Views.Dashboards.Common.FullPeriod")} value={ParticipationListPublicationPeriod.All} />
                    </AppTabs>
                </Grid>
            );
    }

    ///
    private renderHeaserSecondaryOption() {
        return (
            <Grid item>
                <MuiSelectFilter
                    value={this.state.secondaryListOption}
                    onChange={this.handleSecondaryListOption}
                >
                    {
                        this.getSecondaryListOptions().map((v, index) => <MenuItem key={index} value={v.key}>{v.text}</MenuItem>)
                    }
                </MuiSelectFilter>
            </Grid>
        );
    }

    ///
    private renderHeader() {
        return (
            <div id="divList" >
                <MuiContainer margin="dense" >
                    <Grid container justifyContent="space-between" alignItems="center">
                        <Grid item>
                            <Select
                                value={this.state.primaryListOption}
                                onChange={this.handleListOption}
                            >
                                {
                                    this.getPrimaryListOptions().map((v, index) => <MenuItem key={index} value={v.key}>{v.text}</MenuItem>)
                                }
                            </Select>
                        </Grid>

                        {
                            this.props.tile == DashboardCourseAnalysisSummaryColumn.Completed
                                ? this.renderHeaderTab()
                                : this.state.primaryListOption == DashboardCourseAnalysisDetailColumn.EnrollProgress || this.state.primaryListOption == DashboardCourseAnalysisDetailColumn.EnrollCompleted
                                    ? this.renderHeaserSecondaryOption()
                                    : null
                        }
                    </Grid>
                </MuiContainer>
            </div>
        );
    }

    ///
    private renderListBody() {
        const { data: { items }, offset, primaryListOption } = this.state;
        const detailCountText = { plural: this.props.t("Views.Dashboards.Common.DetailCountCourse2"), singular: this.props.t("Views.Dashboards.Common.DetailCountCourse1") };

        console.log(primaryListOption);

        switch (primaryListOption) {

            case DashboardCourseAnalysisDetailColumn.Topics:
                return <RenderTopicModelList
                    indexOffset={offset}
                    items={items}
                    onSelectTopic={this.props.onSelectTopic}
                    onDrillDown={(item) => this.publicationDrillDown(item.topicId, item.name, DashboardCourseAnalysisDetailColumn.TopicDrillDown)}
                />;

            case DashboardCourseAnalysisDetailColumn.UsersMostCompleted:
                return <RenderUserModelList
                    indexOffset={offset}
                    items={items}
                    showDrillColumn={this.props.filter.courseId == null}
                    onDrillDown={(item) => this.publicationDrillDown(item.userId, item.name, DashboardCourseAnalysisDetailColumn.UsersMostCompletedDrillDown)}
                    onSelectUser={this.props.onSelectUser}
                />;

            case DashboardCourseAnalysisDetailColumn.UsersMostAbandon:
                return <RenderUserModelList
                    indexOffset={offset}
                    items={items}
                    showDrillColumn={this.props.filter.courseId == null}
                    onDrillDown={(item) => this.publicationDrillDown(item.userId, item.name, DashboardCourseAnalysisDetailColumn.UsersMostAbandonDrillDown)}
                    onSelectUser={this.props.onSelectUser}
                />;

            case DashboardCourseAnalysisDetailColumn.UsersNotAccess:
                return <RenderUserModelList
                    indexOffset={offset}
                    items={items}
                    showDrillColumn={this.props.filter.courseId == null}
                    onDrillDown={(item) => this.publicationDrillDown(item.userId, item.name, DashboardCourseAnalysisDetailColumn.UsersNotAccessDrillDown)}
                    onSelectUser={this.props.onSelectUser}
                />;

            case DashboardCourseAnalysisDetailColumn.UsersProgressCourse:
                return <RenderUserProgressModelList
                    {...this.props}
                    indexOffset={offset}
                    items={items}
                    onSelectUser={this.props.onSelectUser}
                />;

            case DashboardCourseAnalysisDetailColumn.CoursesMostAbandon:
                return <RenderPublicationModelList
                    indexOffset={offset}
                    items={items}
                    detailCountText={detailCountText}
                    showCreationDate={false}
                    onSelectPublication={this.props.onSelectPublication}
                    onDrillDown={(item) => this.userDrillDown(item.publicationId, item.title, DashboardCourseAnalysisDetailColumn.CoursesMostAbandonDrillDown)}
                />;

            case DashboardCourseAnalysisDetailColumn.CoursesMostCompleted:
                return <RenderPublicationModelList
                    indexOffset={offset}
                    items={items}
                    detailCountText={detailCountText}
                    showCreationDate={false}
                    onSelectPublication={this.props.onSelectPublication}
                    onDrillDown={(item) => this.userDrillDown(item.publicationId, item.title, DashboardCourseAnalysisDetailColumn.CoursesMostCompletedDrillDown)}
                />;

            case DashboardCourseAnalysisDetailColumn.EnrollProgress:
                return this.props.filter.courseId == null
                    ? <RenderPublicationModelList
                        indexOffset={offset}
                        items={items}
                        detailCountText={detailCountText}
                        showCreationDate={false}
                        onSelectPublication={this.props.onSelectPublication}
                        onDrillDown={(item) => this.userDrillDown(item.publicationId, item.title, DashboardCourseAnalysisDetailColumn.EnrollProgressDrillDown)}
                    />
                    : <RenderUserModelList
                        indexOffset={offset}
                        items={items}
                        showDrillColumn={this.props.filter.courseId == null}
                    />;

            case DashboardCourseAnalysisDetailColumn.EnrollCompleted:
                return this.props.filter.courseId == null
                    ? <RenderPublicationModelList
                        indexOffset={offset}
                        items={items}
                        detailCountText={detailCountText}
                        showCreationDate={false}
                        onSelectPublication={this.props.onSelectPublication}
                        onDrillDown={(item) => this.userDrillDown(item.publicationId, item.title, DashboardCourseAnalysisDetailColumn.EnrollCompletedDrillDown)}
                    />
                    : <RenderUserModelList
                        indexOffset={offset}
                        items={items}
                        showDrillColumn={this.props.filter.courseId == null}
                    />;
        }

        return null;
    }

    ///
    private renderList() {
        const { data: { total }, dialog } = this.state;

        console.log(this.state);
        console.log(total);

        return (
            <MuiContainer margin="normal">
                <div>
                    {dialog}
                    <br />
                    {this.renderHeader()}

                    {
                        total == 0
                            ? <MuiMessageBox variant="warning" message={this.res("EmptyData")} />
                            : (
                                <>
                                    <MuiContainer>
                                        <Table size="small">
                                            <TableBody >
                                                {this.renderListBody()}
                                            </TableBody>
                                        </Table>
                                    </MuiContainer>
                                    {
                                        total > 0 &&
                                        <Grid container justifyContent="center" alignItems="center">
                                            <Grid item xs>
                                                <MuiPagination
                                                    limit={DefaultListParameters.viewRecordsByPage}
                                                    offset={this.state.offset}
                                                    total={total}
                                                    onClick={(e, offset) => this.loadData(offset, true)}
                                                />
                                            </Grid>
                                            <Grid item>
                                                <IconButtonDownload onClick={this.handleExport} />
                                            </Grid>
                                        </Grid>
                                    }
                                </>
                            )
                    }
                </div>
            </MuiContainer >
        );
    }

    ///
    private updateListOption() {
        this.setState({ ...this.computeListOptions() }, () => this.loadData(0));
    }

    ///
    private checkForUpdateState(prevProps: ListProps) {
        if (
            !LibUtils.isEqual(this.props.filter, prevProps.filter)
            ||
            this.props.tile !== prevProps.tile
            ||
            this.props.primaryOption !== prevProps.primaryOption
            ||
            (this.props.secondOption !== prevProps.secondOption)
            ||
            (this.props.selectedDay !== prevProps.selectedDay)
        ) {
            this.updateListOption();
        }
    }

    ///
    public componentDidMount() {
        this.updateListOption();
    }

    ///
    public componentDidUpdate(prevProps: ListProps) {
        this.checkForUpdateState(prevProps);
    }

    ///
    public render() {

        return (
            <div>
                {
                    this.state.loading
                        ? <SpinnerIndicator />
                        : this.state.data == null
                            ? <AppMessageBox resource="LoadListDataFailed" />
                            : this.renderList()
                }
            </div>
        );
    }
}

